const END_FE = '}';
const START_FE = '{';
const QUOTE = "'";

class StringReader {
    constructor(input) {
        this.input = input || '';
        this.index = 0;
    }

    advance() {
        if (this.hasMore()) {
            this.index++;
        }
    }

    /**
     * @param {RegExp} test
     * @returns {boolean}
     */
    expectNextR(test) {
        if (this.index >= this.input.length - 1) {
            return false;
        }
        return test.test(this.input.charAt(this.index + 1));
    }

    /**
     * @param {String} test
     * @returns {boolean}
     */
    expectNext(test) {
        if (this.index >= this.input.length - 1) {
            return false;
        }
        return test === this.input.charAt(this.index + 1);
    }

    hasMore() {
        return this.index < this.input.length;
    }

    getChar() {
        return this.input.charAt(this.index);
    }

    /**
     * @param re
     * @returns string
     */
    readWhileR(re) {
        let result = '';
        while (this.hasMore()) {
            if (!re.test(this.getChar())) {
                break;
            }
            result += this.getChar();
            this.advance();
        }
        return result;
    }

    /**
     * @param {RegExp} re
     * @returns string
     */
    readUnlessR(re) {
        let result = '';
        while (this.hasMore()) {
            const next = this.getChar();
            if (re.test(next)) {
                break;
            }
            result += next;
            this.advance();
        }
        return result;
    }

    /**
     * @param {String} re
     * @returns string
     */
    readUnless(re) {
        let result = '';
        while (this.hasMore()) {
            const next = this.getChar();
            if (re === next) {
                break;
            }
            result += next;
            this.advance();
        }
        return result;
    }

    /**
     * @param re
     * @returns string
     */
    skipWhileR(re) {
        while (this.hasMore()) {
            if (!re.test(this.getChar())) {
                break;
            }
            this.advance();
        }
    }

    getRest() {
        return this.input.substring(this.index);
    }

    /**
     * @param {Function} contentTest
     * @param {Boolean} keepQuotedString set true, if your want original quoted string, without unescape
     * @returns {String}
     */
    readBraces(contentTest, keepQuotedString) {
        let mark = this.index;

        if (typeof contentTest !== 'function') {
            contentTest = () => true;
        }
        if (typeof keepQuotedString === "undefined") {
            keepQuotedString = false;
        }


        let content = '';
        let deep = 1;
        this.advance();

        const fail = () => {
            this.index = mark;
            return null;
        };

        while (this.hasMore()) {
            switch (this.getChar()) {
                case QUOTE:
                    const quotedString = this.readQuoted();
                    content += keepQuotedString ? quotedString.raw : quotedString.value;
                    continue;
                case START_FE:
                    deep++;
                    break;
                case END_FE:
                    if (deep === 1) {
                        if (contentTest(content)) {
                            this.advance();
                            return content;
                        } else {
                            return fail();
                        }
                    } else {
                        deep--;
                    }
            }

            content += this.getChar();
            this.advance();
        }

        return fail();
    }

    /**
     * @returns {QuotedString}
     */
    readQuoted() {
        let raw = this.getChar();
        this.advance();

        if (this.hasMore() && this.getChar() === QUOTE) {
            raw += this.getChar();
            this.advance();
            return new QuotedString(QUOTE, raw);
        }

        let value = '';
        while (this.hasMore()) {
            raw += this.getChar();

            if (this.getChar() === QUOTE) {
                this.advance();

                if (this.getChar() === QUOTE) {
                    raw += this.getChar();
                } else {
                    return new QuotedString(value, raw);
                }
            }

            value += this.getChar();
            this.advance();
        }

        return new QuotedString(value, raw);
    }
}

class QuotedString {
    constructor(value, raw) {
        this.value = value;
        this.raw = raw;
    }
}

const ICUMessageFormats = {
    'default': (ordinal) => {
        return (texter, args) => args[ordinal];
    },
    'plural': (ordinal, options) => {
        const optionsReader = new StringReader(options);

        const model = {};
        while (optionsReader.hasMore()) {
            const state = optionsReader.readUnless(START_FE).trim();
            if (state === '') {
                break;
            }
            model[state] = optionsReader.readBraces();
        }

        return (texter, args) => {
            const argsValue = args[ordinal];
            const number = typeof argsValue === "number" ? Math.floor(argsValue) : parseInt(argsValue);

            let state = null;
            if (!isNaN(number)) {
                state = texter.numberPlural(number);
            }

            if (state in model) {
                return model[state];
            }
            return model['other'];
        };
    }
};

/**
 * Javascript port for com.rico.rubd.support.icu.ICUMessageFormat
 */
class ICUMessageFormat {
    constructor(pattern) {
        this.formats = [];

        this.parsePattern(pattern,
            token => this.formats.push(() => token),
            (ordinal, spec, options) => this.formats.push(this.createFormatter(ordinal, spec, options)));
    }

    /**
     * @param {String} pattern
     * @param {Function} textReceiver
     * @param {Function} formatReceiver
     * @private
     */
    parsePattern(pattern, textReceiver, formatReceiver) {
        const reader = new StringReader(pattern);
        let token = '';

        function panic() {
            throw new Error(`invalid pattern ${pattern}`);
        }

        const argumentComplete = (content) => {
            if (!content.length) {
                return;
            }

            const tokenReader = new StringReader(content);

            tokenReader.skipWhileR(/[\s]/);
            const ordinal = parseInt(tokenReader.readWhileR(/\d+/).trim());
            if (isNaN(ordinal)) panic();

            tokenReader.skipWhileR(/[\s,]/);
            const spec = tokenReader.readUnlessR(/,/).trim();

            tokenReader.skipWhileR(/[\s,]/);
            let options = tokenReader.getRest().trim();

            formatReceiver(ordinal, spec, options);
        };

        const textComplete = (token) => {
            if (token.length) {
                textReceiver(token);
            }
        };

        const FE_ORDINAL_TEST = /^\s*\d/;
        while (reader.hasMore()) {
            switch (reader.getChar()) {
                case START_FE:
                    textComplete(token);
                    token = '';

                    const braces = reader.readBraces(FE_ORDINAL_TEST.test.bind(FE_ORDINAL_TEST), true);
                    if (braces == null) {
                        break;
                    }

                    argumentComplete(braces);
                    continue;

                case QUOTE:
                    token += reader.readQuoted().value;
                    continue;
            }
            token += reader.getChar();
            reader.advance();
        }

        textComplete(token);
    }

    /**
     * @param {int} ordinal parameter index
     * @param {string} spec formatter class
     * @param {string} options other parameters
     * @private
     */
    createFormatter(ordinal, spec, options) {
        const factory = spec in ICUMessageFormats
            ? ICUMessageFormats[spec]
            : ICUMessageFormats.default;
        return factory(ordinal, options);
    }

    format(texter, args) {
        switch (this.formats.length) {
            case 0:
                return '';
            case 1:
                return this.formats[0](texter, args);
            default:
                return this.formats.map(f => f(texter, args)).join('');
        }
    }
}

/**
 * https://www.unicode.org/cldr/charts/34/supplemental/language_plural_rules.html
 */
const TexterSpec = {
    defaults: {
        pluralRule: function (n) {
            if (n === 1) {
                return 'one';
            }
            return 'many';
        }
    },

    'ru': {
        pluralRule: function (n) {
            const rem10 = n % 10;
            const rem100 = n % 100;
            if (rem10 === 1 && rem100 !== 11) {
                return 'one';
            }
            if ((rem10 === 2 || rem10 === 3 || rem10 === 4) && rem100 !== 12 && rem100 !== 13 && rem100 !== 14) {
                return 'few';
            }
            return 'many';
        }
    }
};

export default class Texter {
    static create(l18n) {
        return l18n instanceof Texter ? l18n : new Texter(l18n);
    }

    constructor(texts) {
        this.texts = texts || [];
        this.cache = {};
        this.locale = 'en';

        let spec = null;
        if ('_language' in this.texts) {
            spec = TexterSpec[this.texts['_language']];
        }
        this.spec = !!spec ? spec : TexterSpec.defaults;
    }

    /**
     * @param key
     * @returns {String}
     * @private
     */
    getText(key) {
        return key in this.texts ? this.texts[key] : key;
    }

    /**
     * @param key
     * @returns {ICUMessageFormat}
     * @private
     */
    getOrCompileFormatter(key) {
        const cached = this.cache[key];
        if (cached) {
            return cached;
        }
        const pattern = key in this.texts ? this.texts[key] : key;
        return this.cache[key] = new ICUMessageFormat(pattern);
    }

    /**
     * @param key
     * @param args
     * @returns {String}
     */
    get(key, ...args) {
        if (args.length === 0) {
            return this.getText(key);
        }
        const formatter = this.getOrCompileFormatter(key);
        return formatter.format(this, args);
    }

    put(key, value) {
        this.texts[key] = value;
    }

    numberPlural(n) {
        return this.spec.pluralRule(n);
    }
}
